import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import "../styles.css";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/products.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        const formattedProducts = data.map(p => {
          const tag = p.buttonText && p.buttonText.startsWith('$') ? 'premium' : (p.cost || 'free').toLowerCase();
          return {
            id: p.id, // Added for keys and general use
            img: p.overlayImage,
            alt: p.name,
            tag: tag,
            title: p.name,
            description: p.description,
            link: p.buttonLink,
            btnClass: p.cost === 'Premium' ? 'btn btn-product-premium2' : 'btn btn-product',
            btnText: p.buttonText, // Use dynamic button text
            categories: [p.cost, ...(p.tags || []), p.location].filter(Boolean).concat(tag === 'premium' ? 'Premium' : []).concat(tag === 'free' ? 'Free' : []),
            location: p.location, // Added for specific "Any" location check
          };
        });

        setProducts(formattedProducts);
      } catch (e) {
        console.error("Failed to fetch products:", e);
        setError("Failed to load products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const staticCategories = [
    "All Products",
    "Premium",
    "Free",
    "Streamer.bot",
    "YouTube",
    "Twitch",
    "Kick",
    "Emotes",
    "Badges"
  ];
  const [selectedCategory, setSelectedCategory] = useState("All Products");

  const filteredProducts = products.filter(product => {
    if (selectedCategory === "All Products") {
      return true; // Always show if "All Products" is selected
    }

    // Check if the product's general categories list includes the selected category
    const hasCategory = product.categories && product.categories.includes(selectedCategory);

    // If selected category is a platform and product's specific location is "Any", include it
    if (["Twitch", "YouTube", "Kick"].includes(selectedCategory) && product.location === "Any") {
      return true;
    }

    // Otherwise, rely on the general category match
    return hasCategory;
  });

  const renderContent = () => {
    if (loading) {
      return (
        <div className="shop-loading">
          <div className="spinner"></div>
          <p>Loading shop...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="shop-error">
          <p>{error}</p>
        </div>
      );
    }

    return (
      <>
        <div className="shop-introduction">
          <p>Browse my curated collection of tools and resources for streamers. Whether you're just starting or looking to upgrade your setup, you'll find something useful here!</p>
        </div>
        <div className="product-categories" style={{marginBottom: '2rem'}}>
          {staticCategories.map(cat => (
            <div
              key={cat}
              className={`product-category${selectedCategory === cat ? " active" : ""}`}
              onClick={() => setSelectedCategory(cat)}
              style={{ cursor: "pointer" }}
            >
              {cat}
            </div>
          ))}
        </div>
        <div className="product-grid">
          {filteredProducts.map((product, idx) => (
            <div className="product-card" key={product.id || idx}>
              <div className="product-image">
                <img src={product.img} alt={product.alt} />
              </div>
              <div className="product-info">
                <div className={`product-tag ${product.tag === 'premium' ? 'premium2' : product.tag}`}>{product.tag.charAt(0).toUpperCase() + product.tag.slice(1)}</div>
                <h3>{product.title}</h3>
                <p>{product.description}</p>
                <a href={product.link} className={product.btnClass} target="_blank" rel="noopener noreferrer">{product.btnText}</a>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Stream Tools & Overlays"
        description="Download free and premium streaming tools, OBS overlays, Streamer.bot extensions, alerts, and widgets. Enhance your Twitch, YouTube, or Kick stream with ZeMaxi's custom tools."
        keywords="streaming tools, OBS overlays, Streamer.bot extensions, free stream tools, Twitch overlays, stream alerts, stream widgets, content creator tools"
        url="https://zemaxi.com/shop"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="shop-hero">
          <div className="container">
            <h1>Stream <span className="highlight">Tools</span></h1>
            <p>Discover free and premium tools to enhance your stream!</p>
          </div>
        </section>
        <section className="shop-section">
          <div className="container shop-container">
            {renderContent()}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
